import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Home,
  Search,
  ArrowLeft,
  Map,
  FileText,
  Settings,
  HelpCircle,
  Send,
  CheckCircle
} from 'lucide-react';

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isReporting, setIsReporting] = useState(false);
  const [reported, setReported] = useState(false);

  const quickLinks = [
    { icon: Home, label: 'Home', path: '/', color: 'bg-blue-500' },
    { icon: Map, label: 'GIS Dashboard', path: '/app/gis', color: 'bg-green-500' },
    { icon: FileText, label: 'Reports', path: '/app/reports', color: 'bg-purple-500' },
    { icon: Settings, label: 'Settings', path: '/app/settings', color: 'bg-orange-500' }
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // You can implement actual search functionality here
      console.log('Searching for:', searchQuery);
      navigate(`/app?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleReportBrokenLink = () => {
    setIsReporting(true);
    // Simulate API call
    setTimeout(() => {
      setReported(true);
      setTimeout(() => {
        setIsReporting(false);
        setReported(false);
      }, 3000);
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center px-4 py-12">
      <div className="max-w-4xl w-full">
        {/* Main Error Display */}
        <div className="text-center mb-12">
          {/* Animated 404 */}
          <div className="relative inline-block mb-6">
            <h1 className="text-[120px] md:text-[180px] font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 leading-none animate-pulse">
              404
            </h1>
            <div className="absolute inset-0 blur-3xl opacity-30 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500"></div>
          </div>

          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Page Not Found
          </h2>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            The page you're looking for doesn't exist or has been moved.
          </p>

          {/* Show attempted path */}
          <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <code className="text-sm text-gray-700 dark:text-gray-300">
              {location.pathname}
            </code>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-12">
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for pages, reports, or features..."
                className="w-full pl-12 pr-4 py-4 text-lg border border-gray-300 dark:border-gray-600 rounded-xl
                         focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent
                         bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                         placeholder-gray-400 dark:placeholder-gray-500
                         shadow-lg hover:shadow-xl transition-shadow"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700
                         text-white rounded-lg font-medium transition-colors"
              >
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Quick Links */}
        <div className="mb-12">
          <h3 className="text-center text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-6">
            Quick Links
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {quickLinks.map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="group flex flex-col items-center gap-3 p-6 bg-white dark:bg-gray-800
                         rounded-xl border-2 border-gray-200 dark:border-gray-700
                         hover:border-indigo-500 dark:hover:border-indigo-400
                         hover:shadow-lg transition-all duration-200"
              >
                <div className={`${link.color} p-3 rounded-lg group-hover:scale-110 transition-transform`}>
                  <link.icon className="w-6 h-6 text-white" />
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300 group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                  {link.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-gray-800 dark:bg-white
                     text-white dark:text-gray-900 rounded-lg font-medium
                     hover:bg-gray-900 dark:hover:bg-gray-100 transition-colors shadow-lg"
          >
            <ArrowLeft className="w-5 h-5" />
            Go Back
          </button>

          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white rounded-lg font-medium
                     hover:bg-indigo-700 transition-colors shadow-lg"
          >
            <Home className="w-5 h-5" />
            Go Home
          </button>

          <button
            onClick={() => navigate('/help')}
            className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800
                     text-gray-900 dark:text-white border-2 border-gray-300 dark:border-gray-600
                     rounded-lg font-medium hover:border-gray-400 dark:hover:border-gray-500
                     transition-colors shadow-lg"
          >
            <HelpCircle className="w-5 h-5" />
            Get Help
          </button>
        </div>

        {/* Report Broken Link */}
        <div className="text-center">
          {!reported ? (
            <button
              onClick={handleReportBrokenLink}
              disabled={isReporting}
              className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400
                       hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isReporting ? (
                <>
                  <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                  Reporting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Report broken link
                </>
              )}
            </button>
          ) : (
            <div className="inline-flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
              <CheckCircle className="w-4 h-4" />
              Thank you for reporting!
            </div>
          )}
        </div>

        {/* Help Text */}
        <div className="mt-12 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Need assistance? Contact support at{' '}
            <a href="mailto:support@maudgis.com" className="text-indigo-600 dark:text-indigo-400 hover:underline">
              support@maudgis.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
